'use client';
import { CollapsibleSection } from '@hyphengroup/cms-components/components/CollapsibleSection/CollapsibleSection';
import { RawHtml } from '@hyphengroup/cms-components/components/RawHtml/RawHtml';
import SectionButton from '@hyphengroup/cms-components/components/SectionButton';
import { MaterialIconName } from '@hyphengroup/cms-components/types/materialIconName';
import { useBreakpoint } from 'car-insurance/hooks/useBreakpoint';
import classNames from 'classnames';
import React, { useState } from 'react';
import { ComponentTabArray } from '$/(cms)/_modules/cmsService/types';

interface CollapsibleContentTabbedProps {
  __typename: 'ComponentPagesComponentTabbedComponent';
  visible: boolean;
  vertical: boolean;
  tab: ComponentTabArray[];
}

export const CollapsibleContentTabbed: React.FC<
  CollapsibleContentTabbedProps
> = ({ visible, vertical, tab }) => {
  const [activeTabId, setActiveTabId] = useState<string>(tab[0]?.id ?? '');
  const isMobile = useBreakpoint() === 'sm';
  const isActive = (tabId: string) => activeTabId === tabId;
  const activeTab = tab.find((t) => t.id === activeTabId);

  const richTextClassName = classNames(
    'cms-rich-text mhg-alpha-typography-body-1 flex flex-col text-colors/neutral-gray-800 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6',
    isMobile
      ? 'gap-spacing/gap/y-sm pt-spacing/gap/y'
      : 'gap-spacing/gap/x leading-line-height/body-1-relaxed',
  );

  if (!visible || !tab.length) {
    return null;
  }

  const handleTabClick = (id: string) => {
    setActiveTabId(id === activeTabId && isMobile ? '' : id);
  };

  return (
    <div
      className={classNames(
        'my-padding/section-y flex flex-col overflow-hidden rounded-radius/card border border-borders/default bg-fill/card',
        {
          'p-spacing/padding/y': isMobile,
          'p-spacing/padding/x-sm': !isMobile,
        },
      )}
    >
      {vertical ? (
        <div
          className={classNames('flex', {
            'flex-col': isMobile,
            'gap-spacing/gap/y': !isMobile,
          })}
        >
          <div
            role="tablist"
            aria-orientation={vertical ? 'vertical' : 'horizontal'}
            className={classNames({ 'w-full': isMobile, 'w-1/3': !isMobile })}
          >
            {tab.map((tabItem) => (
              <div
                key={tabItem.id}
                className="pb-spacing/gap/soft-8px last:pb-0"
              >
                {isMobile ? (
                  <CollapsibleSection
                    id={tabItem.id}
                    isOpen={activeTabId === tabItem.id}
                    onToggle={() => handleTabClick(tabItem.id)}
                    iconClassName="text-font-size/bullet-1 overflow-visible"
                    iconName={tabItem.icon as MaterialIconName | undefined}
                    content={
                      <RawHtml
                        className={richTextClassName}
                        content={tabItem.content}
                      />
                    }
                  >
                    {tabItem.title}
                  </CollapsibleSection>
                ) : (
                  <SectionButton
                    isOpen={isActive(tabItem.id)}
                    toggleVisibility={() => handleTabClick(tabItem.id)}
                    showExpandIcon={false}
                    className="flex w-full items-center text-center"
                    iconName={tabItem.icon as MaterialIconName | undefined}
                    iconClassName="text-font-size/bullet-1 overflow-visible"
                    verticalAlign
                  >
                    {tabItem.title}
                  </SectionButton>
                )}
              </div>
            ))}
          </div>

          {!isMobile && (
            <div className="w-2/3">
              {activeTab?.content && (
                <RawHtml
                  className={richTextClassName}
                  content={activeTab.content}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={`flex gap-spacing/gap/soft-12px ${isMobile ? 'flex-col' : 'w-full'}`}
          >
            {tab.map((tabItem) => (
              <div key={tabItem.id} className={isMobile ? 'w-full' : 'w-1/2'}>
                {isMobile ? (
                  <CollapsibleSection
                    id={tabItem.id}
                    isOpen={activeTabId === tabItem.id}
                    onToggle={() => handleTabClick(tabItem.id)}
                    iconName={tabItem.icon as MaterialIconName | undefined}
                    iconClassName="text-font-size/bullet-1 overflow-visible"
                    content={
                      <RawHtml
                        className={richTextClassName}
                        content={tabItem.content}
                      />
                    }
                  >
                    {tabItem.title}
                  </CollapsibleSection>
                ) : (
                  <SectionButton
                    isOpen={isActive(tabItem.id)}
                    toggleVisibility={() => handleTabClick(tabItem.id)}
                    showExpandIcon={false}
                    className="flex w-full items-center text-center"
                    iconName={tabItem.icon as MaterialIconName | undefined}
                    iconClassName="text-font-size/bullet-1 overflow-visible"
                  >
                    {tabItem.title}
                  </SectionButton>
                )}
              </div>
            ))}
          </div>

          {!isMobile && (
            <div>
              {activeTab?.content && (
                <RawHtml
                  className={classNames(
                    richTextClassName,
                    'pt-spacing/padding/y',
                  )}
                  content={activeTab.content ?? ''}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
